import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ScrollToTop from "./ScrollToTop";
import ThemeToggleButton from "./ThemeToggleButton";
import "../styles/IssueDetailView.css";
import {
  loadFromCache,
  saveToCache,
  getTimestamp,
  setTimestamp,
  isFresh,
} from "../utils/cache";
import { extractWikiKeywords } from "../utils/extractWikiKeywords";

export default function IssueDetailView() {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [issueDetail, setIssueDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [selectionTooltip, setSelectionTooltip] = useState(null);
  const issueContentRef = useRef(null);

  const defaultArticleCounts = {
    progressive: 0,
    moderate: 0,
    conservative: 0,
    unknown: 0,
  };

  const defaultRepresentatives = {
    progressive: null,
    moderate: null,
    conservative: null,
    unknown: null,
  };

  const summaryFromState = location.state?.summary ?? null;
  const stateArticleCounts =
    location.state?.articleCounts || defaultArticleCounts;
  const stateRepresentatives =
    location.state?.representatives || defaultRepresentatives;

  const hideSelectionTooltip = useCallback(() => {
    setSelectionTooltip(null);
  }, []);

  // 3. 현재 페이지에 필요한 데이터 추출
  const safeDecodeURI = (uri) => {
    try {
      return decodeURI(uri);
    } catch (e) {
      console.error("Failed to decode URI:", uri, e);
      return uri; // Return original URI if decoding fails
    }
  };

  const decodedIssueId = safeDecodeURI(issueId);
  const cacheKey = decodedIssueId ? `cachedIssueDetail:${decodedIssueId}` : null;
  const cacheTimestampKey = cacheKey ? `${cacheKey}:timestamp` : null;

  useEffect(() => {
    if (!cacheKey) return;

    const cached = loadFromCache(cacheKey);
    if (cached && isFresh(getTimestamp(cacheTimestampKey))) {
      setIssueDetail(cached);
      return;
    }

    let isMounted = true;
    setIsLoading(true);
    setDetailError(null);

    (async () => {
      try {
        const response = await fetch(
          `/api/issues/${encodeURIComponent(decodedIssueId)}`
        );

        if (response.status === 404) {
          const fallbackData = {
            keyword: decodedIssueId,
            category: null,
            keyDate: null,
            summary: summaryFromState,
            analysis: null,
            articles: [],
            articleCounts: stateArticleCounts,
            representatives: stateRepresentatives,
          };
          if (isMounted) {
            setIssueDetail(fallbackData);
            saveToCache(cacheKey, fallbackData);
            setTimestamp(cacheTimestampKey);
          }
          return;
        }

        if (!response.ok) {
          throw new Error(`이슈 데이터를 불러오지 못했습니다. (${response.status})`);
        }
        const data = await response.json();
        if (!isMounted) return;
        setIssueDetail(data);
        saveToCache(cacheKey, data);
        setTimestamp(cacheTimestampKey);
      } catch (error) {
        if (!isMounted) return;
        setDetailError(error instanceof Error ? error.message : String(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [cacheKey, cacheTimestampKey, decodedIssueId]);

  useEffect(() => {
    const handleMouseUp = () => {
      window.setTimeout(() => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) {
          hideSelectionTooltip();
          return;
        }

        const rawText = selection.toString().trim();
        if (!rawText) {
          hideSelectionTooltip();
          return;
        }

        const normalizedText = rawText.replace(/\s+/g, " ");
        if (normalizedText.length > 48) {
          hideSelectionTooltip();
          return;
        }
        const words = normalizedText.split(" ");
        if (words.length > 8) {
          hideSelectionTooltip();
          return;
        }

        if (selection.rangeCount === 0) {
          hideSelectionTooltip();
          return;
        }

        const range = selection.getRangeAt(0);
        const containerNode =
          range.commonAncestorContainer?.nodeType === Node.TEXT_NODE
            ? range.commonAncestorContainer.parentNode
            : range.commonAncestorContainer;

        if (
          !issueContentRef.current ||
          !containerNode ||
          !issueContentRef.current.contains(containerNode)
        ) {
          hideSelectionTooltip();
          return;
        }

        const rects = range.getClientRects();
        const rect =
          rects.length > 0
            ? rects[rects.length - 1]
            : range.getBoundingClientRect();

        if (!rect || (rect.width === 0 && rect.height === 0)) {
          hideSelectionTooltip();
          return;
        }

        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        const tooltipWidth = 280;
        const tooltipHeight = 44;

        const preferredTop = rect.top - tooltipHeight - 12;
        const top = preferredTop > 0 ? preferredTop : rect.bottom + 12;

        let left = rect.left + rect.width / 2;
        left = Math.max(tooltipWidth / 2 + 16, left);
        left = Math.min(windowWidth - tooltipWidth / 2 - 16, left);

        setSelectionTooltip({
          text: normalizedText,
          top,
          left,
        });
      }, 0);
    };

    const handleKeyUp = (event) => {
      if (event.key === "Escape") {
        hideSelectionTooltip();
      }
    };

    const handleScroll = () => {
      hideSelectionTooltip();
    };

    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("keyup", handleKeyUp);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideSelectionTooltip]);

  const articles = issueDetail?.articles || [];
  const summary = summaryFromState ?? issueDetail?.summary ?? null;
  const analysis = issueDetail?.analysis || null;
  const articleCounts = issueDetail?.articleCounts || stateArticleCounts;
  const representatives =
    issueDetail?.representatives || stateRepresentatives;

  // --- 이하 기존 렌더링 로직 (일부 수정) ---

  function formatSection(section) {
    return section?.split(/(### |\d\. )/)[0]?.trim()
      ?.replace(/- ?(\*\*)?(입장 및 논리|주요 입장과 논리|핵심 사실)(\*\*)?:?/, '<h3>$2</h3>')
      ?.replace(/- ?(\*\*)?(근거|근거와 출처|출처)(\*\*)?:?/, '<h3>$2</h3>');
  }

  const getBiasClassName = (label) => {
    if (!label) return '';
    if (label.includes('진보')) return 'progressive';
    if (label.includes('중도')) return 'moderate';
    if (label.includes('보수')) return 'conservative';
    return '';
  };

  function renderAnalysis() {
    if (!analysis) return null;

    // politics_issues_20251117.json 구조: summary, positions[{actor, stanceType, arguments[], basis[]}]
    const positions = Array.isArray(analysis.positions) ? analysis.positions : [];
    const summaryText = analysis.summary || null;

    // 기존 분석 구조 병행 지원
    const facts = Array.isArray(analysis.facts) ? analysis.facts : [];
    const perspectives = Array.isArray(analysis.perspectives) ? analysis.perspectives : [];
    const concerns = Array.isArray(analysis.concerns) ? analysis.concerns : [];
    const evaluation = analysis.evaluation || "";

    const hasLegacy =
      facts.length > 0 || perspectives.length > 0 || concerns.length > 0 || Boolean(evaluation);

    return (
      <div className="idv-analysis">
        {summaryText && (
          <div className="idv-analysis-section">
            <h3>요약</h3>
            <p className="idv-evaluation-text">{summaryText}</p>
          </div>
        )}

        {positions.length > 0 && (
          <div className="idv-analysis-section">
            <h3>입장별 논리</h3>
            <div className="idv-positions">
              {positions.map((p, idx) => (
                <div key={idx} className="idv-position-card">
                  <div className="idv-position-header">
                    <span className="idv-position-actor">{p.actor || "입장"}</span>
                    {p.stanceType && <span className="idv-position-stance">{p.stanceType}</span>}
                  </div>
                  {Array.isArray(p.arguments) && p.arguments.length > 0 && (
                    <ul className="idv-position-list">
                      {p.arguments.map((arg, aidx) => (
                        <li key={aidx}>{arg}</li>
                      ))}
                    </ul>
                  )}
                  {Array.isArray(p.basis) && p.basis.length > 0 && (
                    <div className="idv-position-basis">
                      <div className="idv-position-basis-title">근거</div>
                      <ul className="idv-position-list">
                        {p.basis.map((b, bidx) => (
                          <li key={bidx}>{b}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {hasLegacy && (
          <div className="idv-legacy-analysis">
            {facts.length > 0 && (
              <div className="idv-analysis-section">
                <h3>사실관계</h3>
                <ul className="idv-facts-list">
                  {facts.map((f, idx) => (
                    <li key={idx} className="idv-fact-item">
                      <div className="idv-fact-point">- {f.point}</div>
                    </li>
                  ))}
                </ul>
                {(() => {
                  const all = facts
                    .filter(f => Array.isArray(f.sources))
                    .flatMap(f => f.sources)
                    .filter(Boolean);
                  const unique = Array.from(new Set(all));
                  if (unique.length === 0) return null;
                  return (
                    <div className="idv-facts-sources-bottom">
                      <div className="idv-facts-sources-title">출처</div>
                      <div className="idv-facts-sources-links">
                        {unique.map((src, i) => (
                          <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="idv-source-link">[{i + 1}]</a>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {perspectives.length > 0 && (
              <div className="idv-analysis-section">
                <h3>관점별 정리</h3>
                <div className="idv-perspectives">
                  {perspectives.slice()
                    .sort((a, b) => {
                      const order = l => l?.includes('진보') ? 0 : l?.includes('중도') ? 1 : l?.includes('보수') ? 2 : 3;
                      return order(a.label) - order(b.label);
                    }).map((p, pidx) => {
                    const biasClass = getBiasClassName(p.label);
                    return (
                      <div key={pidx} className={`idv-perspective-card ${biasClass}`}>
                        <div className="idv-perspective-label">{p.label || '관점'}</div>
                        <div className="idv-perspective-stance">{p.stance}</div>
                        {Array.isArray(p.evidence) && p.evidence.length > 0 && (
                          <ul className="idv-evidence-list">
                            {p.evidence.slice(0, 3).map((evi, eidx) => (
                              <li key={eidx} className="idv-evidence-item">
                                <span className="idv-evidence-claim">{evi.claim}</span>
                                {evi.source && (
                                  <a href={evi.source} target="_blank" rel="noopener noreferrer" className="idv-source-link">[출처]</a>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {concerns.length > 0 && (
              <div className="idv-analysis-section">
                <h3>쟁점 및 우려</h3>
                <ul className="idv-concerns-list">
                  {concerns.map((c, cidx) => (
                    <li key={cidx}>- {c}</li>
                  ))}
                </ul>
              </div>
            )}

            {evaluation && (
              <div className="idv-analysis-section">
                <h3>평가</h3>
                <p className="idv-evaluation-text">{evaluation}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (isLoading && !issueDetail) {
    return (
      <>
        <div className="idv-issue-detail">
          <div className="idv-issue-content">
            <div className="idv-loading-state" role="status" aria-live="polite">
              <div className="idv-loading-spinner" aria-hidden="true" />
              <div className="idv-loading-text">
                <h2>이슈 데이터를 불러오는 중...</h2>
                <p>최신 분석을 불러오는 동안 잠시만 기다려 주세요.</p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (detailError && !issueDetail) {
    return (
      <>
        <div className="idv-issue-detail">
          <div className="idv-issue-content">
            <button onClick={() => navigate(-1)} className="idv-back-button">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              뒤로 가기
            </button>
            <div className="idv-error-message">
              <h2>{detailError}</h2>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (articles.length === 0) {
    return (
      <>
        <div className="idv-issue-detail">
          <div className="idv-issue-content">
            <button onClick={() => navigate(-1)} className="idv-back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              뒤로 가기
            </button>
            <h1 className="idv-issue-title">{decodedIssueId}</h1>
            <div className="idv-error-message">
              <h2>관련 기사가 없습니다.</h2>
              <p>이슈는 등록되었지만 기사 데이터가 아직 수집되지 않았습니다.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="idv-issue-detail">
        <div className="idv-issue-content" ref={issueContentRef}>
          <div className="idv-title-row">
            <h1 className="idv-issue-title">{decodedIssueId}</h1>
            <span
              className="idv-selection-hint"
              aria-label="의미가 궁금한 부분을 드래그하면 나무위키로 이동이 가능합니다."
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <circle cx="12" cy="16" r="1"></circle>
              </svg>
            </span>
          </div>
          <div className="idv-keyword-links">
            {/* <span className="idv-keyword-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path><line x1="7" y1="7" x2="7.01" y2="7"></line></svg>
            </span> */}
            {/* {extractWikiKeywords(decodedIssueId).map((word, idx) => (
              <a
                key={idx}
                href={`https://namu.wiki/w/${encodeURIComponent(word)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="idv-keyword-link"
              >
                {word}
              </a>
            ))} */}
          </div>
          {selectionTooltip && (
            <div
              className="idv-selection-tooltip"
              style={{
                top: `${selectionTooltip.top}px`,
                left: `${selectionTooltip.left}px`,
              }}
            >
              <span className="idv-selection-tooltip__label">
                {selectionTooltip.text.length > 18
                  ? `${selectionTooltip.text.slice(0, 18)}…`
                  : selectionTooltip.text}
              </span>
              <a
                href={`https://namu.wiki/w/${encodeURIComponent(
                  selectionTooltip.text
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="idv-selection-tooltip__link"
                onClick={() => {
                  window.setTimeout(() => {
                    hideSelectionTooltip();
                  }, 0);
                }}
              >
                나무위키에서 보기
              </a>
            </div>
          )}
          {renderAnalysis()}
          <div className="idv-articles-grid">
            {[
              { label: "진보", key: "progressive" },
              { label: "중도", key: "moderate" },
              { label: "보수", key: "conservative" },
            ].map(({ label, key }) => {
              const biasClass =
                key === "progressive"
                  ? "progressive"
                  : key === "moderate"
                  ? "moderate"
                  : "conservative";

              const biasArticles = articles.filter(
                (entry) => entry.biasCategory === label
              );

              return (
                <div key={key} className="idv-bias-column">
                  <h3 className={`idv-bias-type idv-bias-${biasClass}`}>
                    {label} | {articleCounts[key] || biasArticles.length || 0}
                  </h3>
                  <div className="idv-articles-list">
                    {biasArticles.map((entry, index) => (
                      <a
                        key={`${entry.url}-${index}`}
                        href={entry.sourceUrl || entry.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="idv-article-card-link"
                      >
                        <div className="idv-article-card">
                          <h4>{entry.title}</h4>
                          <p className="idv-article-description">
                            {entry.description}
                          </p>
                          <div className="idv-article-meta">
                            <span className="idv-outlet-name">
                              {entry.outletName || "알 수 없음"}
                            </span>
                            {entry.date && (
                              <span className="idv-article-date">
                                {new Date(entry.date).toLocaleDateString(
                                  "ko-KR",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ThemeToggleButton/>
      <ScrollToTop />
    </>
  );
}
